import axios from 'axios';
import { message } from 'antd';
import React, { ReactNode } from 'react';

interface RateLimitError {
  status: number;
  error: string;
  message: string;
  rateLimitInfo: string;
}

interface MessageContent {
  content: ReactNode;
  duration?: number;
  style?: React.CSSProperties;
}

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

const refreshAccessToken = async (): Promise<string> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://localhost:5443'}/core/refresh-token`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const { accessToken } = await response.json();
    localStorage.setItem('accessToken', accessToken);
    return accessToken;
  } catch (error) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    throw error;
  }
};

axiosInstance.interceptors.request.use(
  (config) => {
    if (config.data instanceof FormData) {
      if (config.headers && config.headers['Content-Type']) {
        delete config.headers['Content-Type'];
      }
    }

    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;

      if (['POST', 'PUT'].includes(config.method?.toUpperCase() || '')) {
        const metadata = {
          clientTimestamp: new Date().toISOString(),
          orgContext: localStorage.getItem('selectedOrganizationId'),
          userId: localStorage.getItem('userId') || '',
        };

        if (!config.data) {
          config.data = { _metadata: metadata };
          if (!config.headers['Content-Type']) {
            config.headers['Content-Type'] = 'application/json';
          }
        }

        else if (typeof config.data === 'string' && config.headers['Content-Type'] === 'application/json') {
          try {
            const data = JSON.parse(config.data);
            data._metadata = metadata;
            config.data = JSON.stringify(data);
          } catch (e) {
            // Not valid JSON, leave as is
          }
        }

        else if (typeof config.data === 'object' && !(config.data instanceof FormData)) {
          config.data._metadata = metadata;
        }

        else if (config.data instanceof FormData) {
          if (metadata.orgContext) config.data.append('orgContext', metadata.orgContext);
          if (metadata.userId) config.data.append('userId', metadata.userId);
          config.data.append('clientTimestamp', metadata.clientTimestamp);
          if (config.headers && config.headers['Content-Type']) {
            delete config.headers['Content-Type'];
          }
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// URLs that are public/open endpoints and should not trigger token refresh
const isPublicEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  // Open endpoints use special tokens, not JWT authentication
  return url.startsWith('/open-') || url.startsWith('open-');
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip token refresh for public endpoints - they use different authentication
    if (error.response?.status === 401 && !originalRequest._retry && !isPublicEndpoint(originalRequest.url)) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await refreshAccessToken();
        processQueue(null, newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 429) {
      const data = error.response.data as RateLimitError;
      const messageContent: MessageContent = {
        content: React.createElement('div', null, [
          React.createElement('p', { key: 'message' }, data.message),
          data.rateLimitInfo &&
          React.createElement('p', {
            key: 'info',
            style: {
              fontSize: '12px',
              marginTop: '4px',
              color: '#ff9494'
            }
          }, data.rateLimitInfo)
        ]),
        duration: 5,
        style: { width: '100%' }
      };
      message.error(messageContent);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;